import { PlatformName } from "@common/models";
import { LogType, LogUnionType } from "@common/models/eventTracking";
import { Issue } from "@common/models/issueTracking";
import {
  mobileDataPromptTemplate,
  promptFinalInstructions,
  promptIntroduction,
  promptVersion,
  webDataPromptTemplate,
} from "@main/pages/shared/helpers/aiAssistantPrompt/prompt";

export interface AiPromptData {
  title: string;
  custom_fields: string;
  metadata: string;
  tags: string;
  time: string;
  priority: string;
  current_view: string;

  screenshot_url: string;
  video_url: string;

  device: string;
  locale: string;
  timezone: string;
  location: string;
  os: string;
  device_orientation: string;
  screen_resolution: string;
  screen_density: string;
  authentication: string;
  nfc_status: string;
  network_status: string;
  font_scale: string;

  battery_status: string;
  low_power_mode: string;
  used_device_memory: string;
  used_app_memory: string;
  storage_status: string;

  app_version: string;
  permissions: string;

  browser: string;
  browse_resolution: string;
  cookies_enabled: string;
  is_mobile: string;

  activity_history: string;
}

export const buildAiPromptFromIssue = (
  issue: Issue,
  activityHistory: LogUnionType[],
  platform?: PlatformName,
): string => {
  const data: AiPromptData = {
    title: sliceString(issue.pretty_title),
    custom_fields: arrayToInlineString(issue.custom_fields, (t) => `${t.label}=${t.value}`),
    metadata: objectToInlineString(issue.metadata_, (v, k) => `${k}=${v}`),
    tags: arrayToInlineString(issue.tags, (t) => t.name, 100),
    time: issue.pretty_time,
    priority: issue.priority.priority,
    current_view: sliceString(issue.current_view, 250),

    screenshot_url: issue.screenshot_url,
    video_url: issue.video_url,

    device: issue.pretty_device_name,
    locale: issue.pretty_locale,
    timezone: issue.pretty_timezone,
    location: issue.pretty_location,
    os: issue.pretty_os,
    device_orientation: issue.pretty_device_orientation,
    screen_resolution: issue.pretty_resolution,
    screen_density: issue.pretty_screen_density,
    authentication: issue.pretty_authentication,
    nfc_status: issue.pretty_nfc,
    network_status: issue.pretty_network_status,
    font_scale: issue.pretty_font_scale,

    battery_status: issue.pretty_battery_status,
    low_power_mode: issue.pretty_low_power_mode,
    used_device_memory: issue.pretty_memory_status,
    used_app_memory: issue.pretty_used_application_memory,
    storage_status: issue.pretty_storage_status,

    permissions: arrayToInlineString(issue.permissions, (p) => p.pretty_name),

    app_version: issue.pretty_app_version,

    browser: issue.pretty_browser,
    browse_resolution: issue.pretty_browser_resolution,
    cookies_enabled: issue.pretty_cookies_enabled,
    is_mobile: issue.pretty_is_mobile,

    activity_history: activityHistoryToString(activityHistory),
  };
  const template = platform === PlatformName.WEB ? webDataPromptTemplate : mobileDataPromptTemplate;
  return buildAiPromptFromTemplate(data, template);
};

export const buildAiPromptFromTemplate = (data: AiPromptData, template: string) => {
  const dataString = template.replace(/{{(.*?)}}/g, (_: any, rawKey: string) => {
    const key = rawKey.trim() as keyof AiPromptData;
    const value = data[key];

    return value !== undefined && value !== null && value !== "" ? String(value) : "Not provided";
  });
  return [promptIntroduction, dataString, promptFinalInstructions, promptVersion].join("\n\n");
};

/** Converts an array of items into an inline string, using a mapper to extract the string representation */
const arrayToInlineString = <T>(
  items?: T[],
  mapper: (item: T) => string = (item) => String(item),
  maxLength = 500,
  separator = ", ",
): string => {
  if (!items || items.length === 0) return "";

  const result = items.map(mapper).join(separator);

  return sliceString(result, maxLength);
};

/** Converts an object into an inline string, using a mapper for key-value pairs */
const objectToInlineString = <T>(
  obj?: Record<string, T>,
  mapper: (value: T, key: string) => string = (v, k) => `${k}=${v}`,
  maxLength = 500,
  separator = ", ",
): string => {
  if (!obj || Object.keys(obj).length === 0) return "";

  const result = Object.entries(obj)
    .map(([k, v]) => mapper(v, k))
    .join(separator);

  return sliceString(result, maxLength);
};

/** Converts report activity history into an inline string */
const activityHistoryToString = (activityHistory: LogUnionType[]): string => {
  return activityHistory
    .filter((item) => {
      switch (item.instance_type) {
        case LogType.NETWORK_REQUEST:
          return item.status_code?.startsWith("4") || item.status_code?.startsWith("5");
        case LogType.CONSOLE_LOG:
          return item.type === "error" || item.type === "warning";
        case LogType.CUSTOM_LOG:
          return item.level === "error" || item.level === "warning";
      }
      return false;
    })
    .slice(0, 5)
    .map((item) => {
      const timestamp: string = new Date(item.timestamp).toLocaleTimeString("en-us", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        fractionalSecondDigits: 3,
      });
      let logLevel, logMessage;

      switch (item.instance_type) {
        case LogType.NETWORK_REQUEST:
          const url: string = sliceString(item.url, 250);
          const responseBody: string = sliceString(item.response_body);
          return `${timestamp} ${url} ${item.method} ${item.status_code} ${responseBody}`;
        case LogType.CONSOLE_LOG:
          logLevel = sliceString(item.type ?? "", 1).toUpperCase();
          logMessage = sliceString(item.message);
          return `${timestamp} ${logLevel} ${logMessage}`;
        case LogType.CUSTOM_LOG:
          logLevel = sliceString(item.level ?? "", 1).toUpperCase();
          logMessage = sliceString(item.message);
          return `${timestamp} ${logLevel} ${logMessage}`;
      }
      return "";
    })
    .join("\n");
};

/** Limits the length of a string, appending ellipsis if necessary */
const sliceString = (str: string, maxLength = 500) => {
  return str.length > maxLength ? str.slice(0, maxLength) + "…" : str;
};
